import { useState, useContext } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
  useTheme,
  useMediaQuery
} from '@mui/material'
import { Box } from '@mui/system'
import DesplegableRubros from './DesplegableRubros'
import { ProductosAdminContext } from '../../context/productos.context'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TenantsContext } from '../../../../context/tenants.context'
import { Dayjs } from 'dayjs'
import { autocomplete } from '../../../../services/autocomplete'
import { Rubro } from '../../../Rubros/AbmRubros/types/rubro'

const AdminAbmProductosFiltros = ({ rubros }: { rubros: Rubro[] }) => {
  const { filtros, setFiltros, setSearching, loading: loadingProductos } = useContext(ProductosAdminContext)
  const [autoCompletado, setAutoCompletado] = useState<string[]>([])

  // Form
  const [codLocal, setCodLocal] = useState<string>(null)
  const [codRubro, setCodRubro] = useState<string>(null)
  const [nombreProducto, setNombreProducto] = useState<string>('')
  const [soloActivos, setSoloActivos] = useState<boolean>(true)
  const [soloARevisar, setSoloARevisar] = useState<boolean>(false)
  const [publicadoDesde, setPublicadoDesde] = useState<Dayjs | null>(null)
  const [publicadoHasta, setPublicadoHasta] = useState<Dayjs | null>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { tenant } = useContext(TenantsContext)

  const handleAutoCompletado = (value: string) => {
    if (value.length < 3) return

    autocomplete(tenant, value, soloActivos)
      .then((result) => {
        setAutoCompletado(result.data.data)
      })
      .catch((error) => {
        console.log('error: ', error)
      })
  }

  const handleFormSubmit = () => {
    setSearching(true)
    const fechaDesde = publicadoDesde?.format('YYYY-MM-DD')
    const fechaHasta = publicadoHasta?.format('YYYY-MM-DD')
    setFiltros({
      ...filtros,
      codLocal,
      codRubro,
      nombreProducto,
      soloActivos,
      soloARevisar,
      publicadoDesde: fechaDesde,
      publicadoHasta: fechaHasta
    })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        width: '100%',
        padding: '10px',
        backgroundColor: '#f5f5f5',
        borderRadius: '5px',
        marginBottom: '10px',
        gap: '8px',

        // display column en mobile
        '@media (max-width: 768px)': {
          flexDirection: 'column',
          gap: '5px'
        }
      }}
    >
      <FormControl sx={{ width: '300px', '@media (max-width: 768px)': { width: '100%' } }}>
        <DesplegableRubros
          rubros={rubros}
          onChange={(r: any) => {
            setCodRubro(r?.codRubro || null)
          }}
          handleFormSubmit={handleFormSubmit}
        />
      </FormControl>

      <FormControl sx={{ width: '300px', '@media (max-width: 768px)': { width: '100%' } }}>
        <Autocomplete
          options={autoCompletado}
          disabled={loadingProductos}
          filterOptions={(x) => x}
          getOptionLabel={(option: any) => option?.nombre || ''}
          isOptionEqualToValue={(option: any, value: any) => option?.data === value?.data}
          onChange={(e, value: any) => setCodLocal(value?.data || null)}
          onInputChange={(e, value, reason) => {
            if (reason === 'input') {
              handleAutoCompletado(value)
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              onKeyPress={(e) => e.key === 'Enter' && handleFormSubmit()}
              label="Local"
              variant="outlined"
            />
          )}
        />
      </FormControl>

      <FormControl sx={{ width: '250px', '@media (max-width: 768px)': { width: '100%' } }}>
        <TextField
          disabled={loadingProductos}
          label="Producto"
          variant="outlined"
          onKeyPress={(e) => e.key === 'Enter' && handleFormSubmit()}
          onChange={(e) => setNombreProducto(e.target.value)}
        />
      </FormControl>

      <FormControl
        sx={{
          width: '220px',
          '@media (max-width: 768px)': { width: '100%' }
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disabled={loadingProductos}
            label={isMobile ? 'Publicado Desde' : 'Public. Desde'}
            format="DD/MM/YYYY"
            value={publicadoDesde}
            onChange={(date: any) => {
              date ? setPublicadoDesde(date) : setPublicadoDesde(null)
            }}
            slotProps={{
              textField: {
                variant: 'outlined',
                onKeyPress: (e) => e.key === 'Enter' && handleFormSubmit(),
                value: publicadoDesde
              }
            }}
          />
        </LocalizationProvider>
      </FormControl>
      <FormControl
        sx={{
          width: '220px',
          '@media (max-width: 768px)': { width: '100%' }
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disabled={loadingProductos}
            label={isMobile ? 'Publicado Hasta' : 'Public. Hasta'}
            format="DD/MM/YYYY"
            value={publicadoHasta}
            onChange={(date: any) => (date ? setPublicadoHasta(date) : setPublicadoHasta(null))}
            slotProps={{
              textField: {
                variant: 'outlined',
                onKeyPress: (e) => e.key === 'Enter' && handleFormSubmit(),
                value: publicadoHasta
              }
            }}
          />
        </LocalizationProvider>
      </FormControl>
      <FormControl
        sx={{
          width: '180px',
          '@media (max-width: 768px)': { width: '100%', m: 0 }
        }}
      >
        <FormControlLabel
          className="m-auto"
          label="Sólo a revisar"
          control={<Checkbox sx={{ paddingLeft: 0 }} />}
          disabled={loadingProductos}
          onChange={(e: any) => setSoloARevisar(e.target.checked)}
        />
      </FormControl>
      <FormControl
        sx={{
          '@media (max-width: 768px)': { width: '100%', m: 0 }
        }}
      >
        <FormControlLabel
          className="m-auto"
          label="Sólo activos"
          control={<Checkbox defaultChecked sx={{ paddingLeft: 0 }} />}
          disabled={loadingProductos}
          onChange={(e: any) => setSoloActivos(e.target.checked)}
        />
      </FormControl>

      <FormControl
        sx={{
          width: '80px',
          paddingRight: '10px',
          '@media (max-width: 768px)': {
            width: '100%'
          }
        }}
      >
        <Button
          disabled={loadingProductos}
          variant="contained"
          onClick={handleFormSubmit}
          sx={{
            height: '50px',
            '@media (max-width: 768px)': {
              display: 'flex',
              gap: '10px'
            }
          }}
        >
          <Box
            sx={{
              display: 'none',
              '@media (max-width: 768px)': {
                display: 'block'
              }
            }}
          >
            Filtrar
          </Box>
          <SearchIcon style={{ margin: 0 }} />
        </Button>
      </FormControl>
    </Box>
  )
}

export default AdminAbmProductosFiltros
